import { useState } from 'react'
import {
  Box, Typography, Grid, TextField, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, CircularProgress,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { AccountBalanceWallet as AccountBalanceWalletIcon } from '@mui/icons-material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import SectionHeader from './SectionHeader'
import { useSubscription, FEATURES } from '../../../../contexts/SubscriptionProvider'

// Hardcoded fallback — used only if API pricing hasn't loaded yet
const PLAN_PRICES_FALLBACK = {
  starter: { monthly: '$124/mo', annual: '$99/mo' },
  pro:     { annual: '$199/user/mo' },
}

const PLAN_DISPLAY_NAMES = {
  starter: 'Essentiel',
  pro: 'Pro',
  enterprise: 'Entreprise',
}

const CancelConfirmationDialog = ({ open, onClose, onConfirm, isCancelling, theme }) => {
  const { t } = useTranslation()

  return (
    <Dialog
      open={open}
      onClose={isCancelling ? undefined : onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 20px rgba(0,0,0,0.5)'
            : '0 4px 20px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ p: 1, mr: 2, borderRadius: '50%', backgroundColor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
            <WarningAmberIcon fontSize="large" />
          </Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {t('confirm_cancel_title', 'Cancel Subscription')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ color: theme.palette.text.primary, mb: 2 }}>
          {t('confirm_cancel_subscription', 'Are you sure you want to cancel your subscription?')}
        </DialogContentText>
        <DialogContentText sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem' }}>
          {t('cancel_subscription_warning', 'You will still have access until the end of your current billing period, but your subscription will not renew automatically.')}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isCancelling} sx={{ mr: 1, textTransform: 'none', fontWeight: 600, color: theme.palette.text.secondary }}>
          {t('back')}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isCancelling}
          variant="contained"
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, bgcolor: theme.palette.error.main, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.8) } }}
        >
          {isCancelling && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
          {isCancelling ? t('cancelling') : t('cancel_subscription')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const UsageBar = ({ feature, label, theme }) => {
  const { getUsage } = useSubscription()
  const usage = getUsage(feature)
  if (!usage || usage.limit === 0) return null

  const percentage = Math.min((usage.used / usage.limit) * 100, 100)
  const isAtLimit = usage.used >= usage.limit
  const isNearLimit = percentage >= 80

  let barColor = theme.palette.primary.main
  if (isAtLimit) barColor = theme.palette.error.main
  else if (isNearLimit) barColor = '#f59e0b'

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}>
          {usage.used} / {usage.limit}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: alpha(barColor, 0.12),
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            bgcolor: barColor,
          },
        }}
      />
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
        {isAtLimit
          ? 'Limit reached — resets next month'
          : `${usage.remaining} remaining this month`
        }
      </Typography>
    </Box>
  )
}

const SubscriptionDetails = ({ subscription, formatDate, handleCancelSubscription, theme }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const apiPricing = useSelector(state => state.pricing?.data)

  const isTrialStatus = subscription?.status === 'trial'
  const isMonthlySubscription = subscription?.billing_cycle === 'monthly'

  const planLabel = subscription?.plan
    ? (PLAN_DISPLAY_NAMES[subscription.plan] || subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1))
    : '—'
  const isProPlan = subscription?.plan === 'pro'
  const seatCount = subscription?.quantity || 1
  const cycleLabel = subscription?.billing_cycle === 'annual' ? t('yearly') : t('monthly')

  // Build price label from API data or fallback
  const getPriceLabel = () => {
    const plan = subscription?.plan
    const cycle = subscription?.billing_cycle
    if (!plan || !cycle) return '—'

    if (apiPricing?.plans) {
      const planData = apiPricing.plans.find(p => p.slug === plan)
      if (planData) {
        const price = cycle === 'annual' ? planData.annual_price : planData.monthly_price
        if (price == null) return '—'
        const sym = apiPricing.currency_symbol || '$'
        const pos = apiPricing.symbol_position || 'prefix'
        const formatted = pos === 'prefix' ? `${sym}${price}` : `${price} ${sym}`
        return `${formatted}/mo`
      }
    }
    return PLAN_PRICES_FALLBACK[plan]?.[cycle] || '—'
  }
  const priceLabel = getPriceLabel()

  const subscriptionFields = [
    { name: 'plan',          label: 'Plan',                    value: planLabel },
    { name: 'status',        label: t('status.label'),          value: subscription?.status ? subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) : '' },
    { name: 'billing_cycle', label: t('subscription_type'),    value: cycleLabel },
    { name: 'price',         label: t('price'),                value: priceLabel },
    ...(isProPlan ? [{ name: 'seats', label: t('seats', 'Seats'), value: `${seatCount} ${seatCount === 1 ? 'user' : 'users'}` }] : []),
    { name: 'start_date',    label: t('start_date'),           value: formatDate(subscription?.start_date) },
    {
      name: 'next_billing',
      label: isTrialStatus ? 'Trial Ends' : t('next_billing_date'),
      value: formatDate(isTrialStatus ? subscription?.trial_end : subscription?.next_billing_date),
    },
  ]

  const getRemainingDaysCount = () => {
    const date = isTrialStatus ? subscription?.trial_end : subscription?.next_billing_date
    if (!date) return 0
    const diffTime = new Date(date) - new Date()
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))
  }

  const getRemainingDays = () => {
    const days = getRemainingDaysCount()
    if (!subscription?.next_billing_date && !subscription?.trial_end) return t('subscription_cancelled')
    if (days > 0) return `${t('subscription_ends_in', { days })}`
    return t('subscription_ended')
  }

  const confirmCancelSubscription = async () => {
    setIsCancelling(true)
    try {
      await handleCancelSubscription()
    } finally {
      setIsCancelling(false)
      setCancelDialogOpen(false)
    }
  }

  const handleSwitchToAnnual = () => {
    const plan = subscription?.plan || 'starter'
    navigate(`/checkout?plan=${plan}&billing=annual&action=upgrade_monthly_to_annual`, {
      state: {
        selectedPlan: { planType: plan, subscriptionType: 'annual' },
        action: 'upgrade_monthly_to_annual',
        fromPlan: plan,
        remainingDays: getRemainingDaysCount(),
        currentSubscriptionId: subscription?.id,
      }
    })
  }

  const canCancel = ['active', 'trial', 'payment_failed'].includes(subscription?.status)

  return (
    <>
      {/* Trial banner */}
      {isTrialStatus && subscription?.trial_end && (
        <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, bgcolor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <InfoOutlinedIcon sx={{ color: '#f59e0b', flexShrink: 0, mt: '2px' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
              {t('trial_banner_title', { date: formatDate(subscription.trial_end) })}{' '}
              {subscription.stripe_subscription_id
                ? t('trial_banner_paid')
                : t('trial_banner_no_payment')
              }
            </Typography>
            {!subscription.stripe_subscription_id && (
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/subscribe')}
                sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
              >
                {t('subscribe_now')}
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <SectionHeader icon={<AccountBalanceWalletIcon />} title={t('subscription_details')} theme={theme} />

        {subscription?.status === 'cancelled' ? (
          <Typography component="span" sx={{ color: theme.palette.text.secondary, fontSize: '0.9rem', fontWeight: 500, py: 0.75, px: 1.5, borderRadius: '6px', bgcolor: theme.palette.background.default }}>
            {getRemainingDays()}
          </Typography>
        ) : canCancel ? (
          <Typography
            component="span"
            sx={{ color: theme.palette.error.main, textDecoration: 'underline', cursor: 'pointer', fontWeight: 500, transition: 'opacity 0.2s', '&:hover': { opacity: 0.7 } }}
            onClick={() => setCancelDialogOpen(true)}
          >
            {t('cancel_subscription')}
          </Typography>
        ) : null}
      </Box>

      <Grid container spacing={3}>
        {subscriptionFields.map((field) => (
          <Grid item xs={12} sm={6} md={4} key={field.name}>
            <TextField
              label={field.label}
              variant="outlined"
              fullWidth
              value={field.value}
              InputProps={{
                readOnly: true,
                sx: {
                  bgcolor: theme.palette.background.default,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                }
              }}
              sx={{ borderRadius: '8px' }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Usage */}
      <Box sx={{ mt: 3 }}>
        <UsageBar feature={FEATURES.AI_CHAT} label={t('ai_chat_usage', 'AI Chat')} theme={theme} />
      </Box>

      {/* Switch to Annual promo — only for active monthly subscribers */}
      {isMonthlySubscription && subscription?.status === 'active' && (
        <Box sx={{ mt: 4, p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 0.5 }}>
              {t('annual_discount_title')}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
              {t('annual_discount_description')}
            </Typography>
          </Box>
          <Button
            variant="contained" disableElevation onClick={handleSwitchToAnnual}
            sx={{ bgcolor: theme.palette.primary.main, color: '#fff', borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 1, px: 3, '&:hover': { bgcolor: theme.palette.primary.dark } }}
          >
            {t('upgrade_to_annual')}
          </Button>
        </Box>
      )}

      <CancelConfirmationDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={confirmCancelSubscription}
        isCancelling={isCancelling}
        theme={theme}
      />
    </>
  )
}

export default SubscriptionDetails
